import { isAxiosError } from 'axios'
import { axiosInstance } from '@/api/api'
import { carrierPortalPages } from '@/router/carrier-portal-pages'

export const CARRIER_BLOCKED_ERROR_CODE = 'carrier_blocked'

// Not in utils/carrier-portal.ts — that would create a router → guards → utils/carrier-portal → router cycle.
export function isCarrierBlockedError(error: unknown): boolean {
  return isAxiosError(error) && error.response?.data?.code === CARRIER_BLOCKED_ERROR_CODE
}

// Surfaces that are themselves inaccessible to blocked carriers. Routes whose chrome is shared
// with open surfaces (privacy/unsubscribe preferences) must never be listed — blocked carriers
// have to stay able to unsubscribe there.
const carrierBlockedRedirectPages = [
  ...carrierPortalPages,
  'carrier-chash-auction-hash',
  'carrier-verification',
]

// The blocked page's guard verifies blocking with an API call that itself 403s, and this
// interceptor sees it while the origin page is still current — reacting to the guard's own 403
// would cancel and restart the navigation in an infinite request loop.
let redirectInFlight = false

// Catches carriers blocked mid-session, after the route guards already let them in.
export function useCarrierBlockedRedirect(): void {
  const router = useRouter()

  const interceptorId = axiosInstance.interceptors.response.use(undefined, (error) => {
    const routeName = router.currentRoute.value.name

    if (
      isCarrierBlockedError(error)
      && !redirectInFlight
      && typeof routeName === 'string'
      && carrierBlockedRedirectPages.includes(routeName)
    ) {
      redirectInFlight = true
      void router.replace({ name: 'carrier-blocked' }).finally(() => {
        redirectInFlight = false
      })
    }

    return Promise.reject(error)
  })

  onUnmounted(() => {
    axiosInstance.interceptors.response.eject(interceptorId)
  })
}
